import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { COVERAGE_FIELDS, badgeClass } from "@/lib/config";
import RecordDialog from "@/components/RecordDialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { fmtMoney, fmtDate } from "@/components/DataTable";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ArrowLeft, RefreshCw, Plus, FileText, Layers } from "lucide-react";

const TERM_FIELDS = [
  { key: "effective_date", label: "Effective Date", type: "date", required: true },
  { key: "expiration_date", label: "Expiration Date", type: "date", required: true },
  { key: "transaction_type", label: "Transaction Type", type: "select", options: { static: ["New Business", "Renewal", "Endorsement", "Reinstatement", "Historical"] } },
  { key: "premium", label: "Premium", type: "number" },
  { key: "status", label: "Status", type: "select", options: { static: ["In Force", "Expired", "Cancelled", "Historical"] } },
  { key: "source", label: "Source", type: "text" },
];

const RENEW_FIELDS = [
  { key: "effective_date", label: "New Effective Date", type: "date", required: true },
  { key: "expiration_date", label: "New Expiration Date", type: "date", required: true },
  { key: "premium", label: "New Premium", type: "number" },
];

export default function PolicyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { can } = useAuth();
  const [policy, setPolicy] = useState(null);
  const [terms, setTerms] = useState([]);
  const [dialog, setDialog] = useState(null);

  const load = useCallback(async () => {
    try {
      const [pr, tr] = await Promise.all([api.get(`/policies/${id}`), api.get(`/policies/${id}/terms`)]);
      setPolicy(pr.data); setTerms(tr.data.items);
    } catch { toast.error("Could not load policy."); }
  }, [id]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (policy) api.post("/agency/recent", { resource: "policies", record_id: policy.id, label: `${policy.policy_number || "Policy"} — ${policy.carrier_name || ""}`.trim(), party_id: policy.party_id }).catch(() => {});
  }, [policy?.id]);

  if (!policy) return <div className="p-6 text-mutedsea">Loading policy…</div>;

  return (
    <div className="p-6 animate-fade-in">
      <button onClick={() => policy.party_id ? nav(`/clients/${policy.party_id}`) : nav("/policies")} data-testid="back-btn" className="text-sm text-atlantic hover:text-ocean flex items-center gap-1 mb-3 nav-link">
        <ArrowLeft className="h-4 w-4" /> Back
      </button>

      <div className="bg-white border border-border rounded-sm p-5 mb-5">
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="text-xs uppercase tracking-wide text-mutedsea flex items-center gap-1.5"><FileText className="h-4 w-4" /> {policy.line}</div>
            <h1 className="font-serif text-3xl text-navy mt-1">{policy.policy_number}</h1>
            <div className="flex flex-wrap gap-2 mt-2 text-sm text-slate">
              <span>{policy.carrier_name}</span>·<span>{policy.named_insured}</span>
              <Badge variant="outline" className={badgeClass(policy.status)}>{policy.status}</Badge>
            </div>
          </div>
          <div className="text-right">
            <div className="font-serif text-2xl text-navy">{fmtMoney(policy.premium)}</div>
            <div className="text-xs text-mutedsea">Current Premium · Expires {fmtDate(policy.expiration_date)}</div>
            {can.write && (
              <div className="flex gap-2 mt-3 justify-end">
                <Button size="sm" data-testid="add-term" variant="outline" onClick={() => setDialog({ kind: "term" })} className="border-atlantic text-atlantic"><Plus className="h-4 w-4 mr-1" /> Historical Term</Button>
                <Button size="sm" data-testid="renew-policy" onClick={() => setDialog({ kind: "renew" })} className="bg-navy hover:bg-atlantic text-white"><RefreshCw className="h-4 w-4 mr-1.5" /> Renew</Button>
              </div>
            )}
          </div>
        </div>
      </div>

      <h2 className="font-serif text-2xl text-navy mb-3 flex items-center gap-2"><Layers className="h-5 w-5 text-atlantic" /> Effective-Dated Policy Terms</h2>
      <p className="text-sm text-mutedsea mb-3">Each renewal creates a new term. Prior terms are preserved permanently and never overwritten.</p>

      <div className="space-y-3">
        {terms.map((t) => (
          <div key={t.id} className="bg-white border border-border rounded-sm" data-testid={`term-${t.id}`}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-cloud">
              <div className="flex items-center gap-3 text-sm">
                <Badge variant="outline" className={badgeClass(t.status)}>{t.status}</Badge>
                <span className="font-medium text-slate">{fmtDate(t.effective_date)} → {fmtDate(t.expiration_date)}</span>
                <span className="text-mutedsea">{t.transaction_type}</span>
                <span className="text-mutedsea">{fmtMoney(t.premium)}</span>
              </div>
              {can.write && <Button size="sm" variant="ghost" data-testid={`add-coverage-${t.id}`} onClick={() => setDialog({ kind: "coverage", termId: t.id })} className="h-7 text-xs text-atlantic"><Plus className="h-3.5 w-3.5 mr-1" /> Coverage</Button>}
            </div>
            <div className="p-3">
              {(!t.coverages || t.coverages.length === 0) ? <p className="text-sm text-mutedsea">No coverages recorded on this term.</p> :
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-border">{["Coverage", "Limit", "Deductible", "Status"].map((h) => <th key={h} className="text-left text-[11px] uppercase text-mutedsea font-semibold px-2 py-1">{h}</th>)}</tr></thead>
                  <tbody>{t.coverages.map((cv) => (
                    <tr key={cv.id} className="border-b border-border last:border-0">
                      <td className="px-2 py-1.5 text-slate">{cv.coverage_type}</td><td className="px-2 py-1.5">{cv.limit || "—"}</td>
                      <td className="px-2 py-1.5">{cv.deductible || "—"}</td><td className="px-2 py-1.5">{cv.status && <Badge variant="outline" className={badgeClass(cv.status)}>{cv.status}</Badge>}</td>
                    </tr>))}</tbody>
                </table>}
            </div>
          </div>
        ))}
      </div>

      {dialog?.kind === "renew" && (
        <RecordDialog open onOpenChange={() => setDialog(null)} fields={RENEW_FIELDS} title="Renew Policy (creates new term)"
          initial={{}} endpoint={`/policies/${id}/renew`} method="post" onSaved={() => { setDialog(null); load(); toast.success("Renewed. Prior term preserved."); }} />
      )}
      {dialog?.kind === "term" && (
        <RecordDialog open onOpenChange={() => setDialog(null)} fields={TERM_FIELDS} title="Add Historical Policy Term"
          initial={{ policy_id: id, party_id: policy.party_id, transaction_type: "Historical", status: "Historical" }} endpoint="/policy_terms" method="post" onSaved={() => { setDialog(null); load(); }} />
      )}
      {dialog?.kind === "coverage" && (
        <RecordDialog open onOpenChange={() => setDialog(null)} fields={COVERAGE_FIELDS} title="Add Coverage"
          initial={{ policy_term_id: dialog.termId, policy_id: id, party_id: policy.party_id }} endpoint="/coverages" method="post" onSaved={() => { setDialog(null); load(); }} />
      )}
    </div>
  );
}
